
import { useSelector } from "react-redux";
import { getSelectedCountry } from "../states/slices/resourceSlice";
import { useMemo, useState } from "react";
import { ITablePaginationData, ITableRowAction } from "../components/elements/GTable/GTable";
import { useSearchParams } from "react-router-dom";
import { Insurer } from "../models/insurer/Insurer";
import { CheckCircle2, EllipsisVerticalIcon, XCircle } from "lucide-react";
import PopOnMe from "../components/elements/poponme/PopOnMe";
import { getPhoneNumberFromShortCodeFormat, showToast } from "../utils/miscFunctions";
import PolicyStatus from "../components/elements/PolicyStatus";
import { getAllInsurers, toggleInsurerStatus } from "../services/insurer";
import { useQueryClient } from "@tanstack/react-query";

type IUseInsurer = {
    getTableRowActions?: (data: any) => ITableRowAction[];
}

export const useInsurer = ({ getTableRowActions }: IUseInsurer) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const countryId = useSelector(getSelectedCountry)?.id;
    const [currentPage, setCurrentPage] = useState<number>(searchParams.get('page') ? Number(searchParams.get('page')) : 0);
    const invalidateInsurerQuery = useQueryClient().invalidateQueries({queryKey: ['getInsurer']});

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        setSearchParams({ page: String(page) });
        getInsurers();
    };

    const getInsurers = async () => {
        if (!countryId) return;

        try {
            const response: any = await getAllInsurers(countryId);
            return response;
        } catch (error: any) {
            showToast.error(error?.description ?? error.message);
        } finally {
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'Active':
                return <CheckCircle2 className="h-5 w-5 text-green-500" />;
            case 'Inactive':
                return <XCircle className="h-5 w-5 text-red-500" />;
            default:
                return null;
        }
    }

    const columns = useMemo(() => {
        const baseColumns = [
            {
                Header: 'Name of Company',
                accessor: 'name',
                Cell: (row: any) => {
                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
            },
            {
                Header: 'Email',
                accessor: 'email',
                Cell: (row: any) => {
                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
            },
            {
                Header: 'Phone',
                accessor: 'phoneNumber',
                Cell: (row: any) => {
                    return (
                        <span>
                            {row.cell.value ? getPhoneNumberFromShortCodeFormat(row.cell.value) : '-'}
                        </span>
                    );
                }
            },
            {
                Header: 'Contact Person',
                accessor: 'contactPerson',
                Cell: (row: any) => {
                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
            },
            {
                Header: 'Website',
                accessor: 'website',
                Cell: (row: any) => {
                    return <span>{row.cell.value ? row.cell.value : '-'}</span>;
                }
            },
            {
                Header: 'Status',
                accessor: 'status',
                Cell: (row: any) => {
                    return (
                        <div className='flex items-center'>
                            {row.cell.value === 'ACTIVE' ? getStatusIcon('Active') : getStatusIcon('Inactive')}
                            <PolicyStatus
                                classes='!w-[50px]'
                                status={row.cell.value === 'ACTIVE' ? 'Active' : 'Inactive'}
                            />
                        </div>
                    );
                }
            }
        ];

        const actionColumn = getTableRowActions ? [{
            id: 'actions',
            Cell: (row: any) => (
                <div className="cursor-pointer flex justify-center">
                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                        <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                </div>
            )
        }] : [];

        return [
            {
                Header: 'M',
                columns: [...baseColumns, ...actionColumn]
            }
        ];
    }, [getTableRowActions, getStatusIcon]);

    const publishInsurer = (insurer: any) => {
        toggleInsurerStatus(insurer?.id ?? '', true)
            .then((response: any) => {
                showToast.success(response?.message ?? 'Store published successfully.');
                if (insurer?.status != response?.data?.status) {
                    invalidateInsurerQuery
                }
            })
            .catch((error: any) => {
                showToast.error(error?.description ?? 'Failed to publish your store. Please try again later');
            });
    };

    const unPublishInsurer = (insurer: any) => {
        toggleInsurerStatus(insurer?.id ?? '', false)
            .then((response: any) => {
                showToast.success(response?.message ?? 'Store unpublished successfully.');

                if (insurer?.status != response?.data?.status) {
                    invalidateInsurerQuery
                }
            })
            .catch((error: any) => {
                showToast.error(error?.description ?? 'Failed to unpublish your store. Please try again later');
            });
    };


    return {
        getInsurers,
        getStatusIcon,
        columns,
        handlePageChange,
        publishInsurer,
        unPublishInsurer
    }
}

